import { Form, Input, Popconfirm, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import { t } from '../../../../../helpers/i18n';
import './RemoteConfigTable.scss';
import { DeleteOutlined } from '@ant-design/icons';
import AddConfigParamPopup from '../AddConfigParamPopup/AddConfigParamPopup';
import { AddIcon } from 'v2/containers/Icons';

enum ConfigColumnType {
  Key,
  Value,
  Action,
}

interface EditableCellProps {
  columnType: ConfigColumnType;
  value: string;
  record: DataItem;
  handleSave: (record: DataItem) => void;
}

const EditableCell: React.FC<EditableCellProps> = (props) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(props.value);

  const save = async () => {
    try {
      setEditing(false);
      props.handleSave({ ...props.record, jsonValue: value });
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  return props.columnType === ConfigColumnType.Value ? (
    <Form.Item style={{ margin: 0 }}>
      <Input
        onFocus={() => setEditing(true)}
        bordered={editing}
        onPressEnter={save}
        onBlur={save}
        value={value}
        onChange={onChange}
      />
    </Form.Item>
  ) : (
    <Form.Item style={{ margin: 0 }}>
      <Input bordered={false} readOnly={true} value={props.record.jsonKey} />
    </Form.Item>
  );
};

type DataItem = {
  jsonKey: string;
  jsonValue: string;
};

interface RemoteConfigTableProps {
  extraConfig: any;
  onTableConfigChanged: (config: any) => void;
}

const RemoteConfigTable: React.FC<RemoteConfigTableProps> = (props) => {
  const [showAddRemoteConfigPopup, setShowAddRemoteConfigPopup] =
    useState<boolean>(false);
  const { extraConfig } = props;
  const [dataSource, setDataSource] = useState<DataItem[]>([]);

  const columns = [
    {
      title: t('create_project_remote_config_table_key'),
      dataIndex: 'key',
      render: (_: any, rowItem: DataItem) => {
        return (
          <EditableCell
            columnType={ConfigColumnType.Key}
            handleSave={handleSave}
            record={rowItem}
            value={rowItem.jsonKey}
          />
        );
      },
    },

    {
      title: t('create_project_remote_config_table_value'),
      dataIndex: 'value',
      width: '50%',
      render: (_: any, rowItem: DataItem) => (
        <EditableCell
          value={rowItem.jsonValue}
          columnType={ConfigColumnType.Value}
          handleSave={handleSave}
          record={rowItem}
        />
      ),
    },
    {
      title: '',
      dataIndex: 'operation',
      width: 80,
      render: (_: any, record: DataItem) =>
        dataSource.length >= 1 ? (
          <Popconfirm
            title={t('create_project_remote_config_delete_message')}
            onConfirm={() => handleDelete((record as DataItem).jsonKey)}
          >
            <div className='delete_icon_container'>
              <DeleteOutlined
                id="guide-header-icon"
                className="normal-icon-style"
              />
            </div>
          </Popconfirm>
        ) : null,
    },
  ];

  useEffect(() => {
    if (extraConfig) {
      const newData = Object.keys(extraConfig).map((jsonKey) => {
        const value = extraConfig[jsonKey];
        return {
          jsonKey: jsonKey,
          jsonValue: value ? value.toString() : '',
        };
      });

      setDataSource(newData);
    }
  }, [extraConfig]);

  const handleAdd = (newKey?: string, newValue?: string) => {
    if (!newKey) return;

    const newData: DataItem = {
      jsonKey: newKey,
      jsonValue: newValue ?? '',
    };
    setDataSource([...dataSource, newData]);

    const newExtraConfig = extraConfig ?? {};
    newExtraConfig[newKey] = newValue ?? '';
    props.onTableConfigChanged(newExtraConfig);
  };

  const handleDelete = (key: string) => {
    const newData = dataSource.filter((item) => item.jsonKey !== key);
    setDataSource(newData);

    delete extraConfig[key];
    props.onTableConfigChanged(extraConfig);
  };

  const handleSave = (row: DataItem) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.jsonKey === item.jsonKey);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });

    setDataSource(newData);
    extraConfig[row.jsonKey] = row.jsonValue;
    props.onTableConfigChanged(extraConfig);
  };

  function showAddParamPopup() {
    setShowAddRemoteConfigPopup(true);
  }

  return (
    <div>
      <Table
        pagination={false}
        rowKey={(row) => row.jsonKey}
        columns={columns}
        dataSource={dataSource}
        bordered
        className='remote-config-table'
      />
      <div className='add_icon_container' onClick={showAddParamPopup}>
        {AddIcon}
      </div>
      {showAddRemoteConfigPopup && (
        <AddConfigParamPopup
          handleCloseModal={(newKey?: string, newValue?: string) => {
            setShowAddRemoteConfigPopup(false);
            handleAdd(newKey, newValue);
          }}
          existingKeys={extraConfig ? Object.keys(extraConfig) : []}
        />
      )}
    </div>
  );
};

export default RemoteConfigTable;
